import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Button,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  ModalHeader,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';

import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import useForm from '../../utils/useForm';
import { cancelSubscription, disableAutorenew } from '../api';
import { useGlobalContext } from '../state/GlobalContext';
import { useLoadingToast } from '../../utils/useLoadingToast';
import { startMomentsApplicationsRefetchBackoff } from '../../utils/momentsRefetchBackoff';

interface ConfirmCancellationModalForm {
  details: string;
}

interface BaseMutationQuery {
  customerId?: string;
  subscriptionId?: string;
  authToken?: string;
}

const ConfirmCancellationModal: React.FC<{
  isEligibleForPrepaidCancellation: boolean;
  shouldExpireContract: boolean;
}> = ({ isEligibleForPrepaidCancellation, shouldExpireContract }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { data, loadPreviousFlowStep, onClose, loadNextFlowStep, setData } =
    useModalManagerContext();
  const { authToken, customerId, subscriptionId, shopHasActiveMoments } =
    useGlobalContext();
  const queryClient = useQueryClient();
  const { values, handleChange } = useForm<ConfirmCancellationModalForm>({
    initialValues: {
      details: '',
    },
  });
  useEffect(() => {
    setData((data) => ({ ...data, details: values.details }));
  }, [setData, values.details]);

  const { addLoadingToast, removeLoadingToast } = useLoadingToast();

  const callCancelSubscription = ({
    customerId,
    subscriptionId,
    authToken,
  }: BaseMutationQuery) => {
    setLoading(true);
    addLoadingToast();
    return shouldExpireContract
      ? disableAutorenew(
          customerId,
          subscriptionId,
          authToken,
          data.reason,
          values.details,
        )
      : cancelSubscription(
          customerId,
          subscriptionId,
          authToken,
          data.reason,
          values.details,
        );
  };

  const onCancelSubscriptionMutationSuccess = async () => {
    const previousSubscriptionValue = queryClient.getQueryData<Subscription>([
      'subscription',
      customerId,
      subscriptionId,
    ]);
    removeLoadingToast();

    queryClient.setQueryData(['subscription', customerId, subscriptionId], {
      ...previousSubscriptionValue,
      SubscriptionStatus: shouldExpireContract ? 'EXPIRED' : 'CANCELLED',
      ItemTypeSubscriptionStatusNextBillingDate: 'SC#CANCELLED#',
      CancelDate: new Date(),
      CancelReason: 'SHOPPER_CANCELLED',
    });
    if (shopHasActiveMoments) {
      startMomentsApplicationsRefetchBackoff({
        queryClient,
        subscriptionId,
      });
    }
    onClose(t('cancelModal.confirm.success'));
  };

  const onCancelSubscriptionMutationError = () => {
    removeLoadingToast();

    toast({
      isClosable: true,
      position: 'top',
      status: 'error',
      title: t('subscription.cancel_error'),
      variant: 'error',
    });
  };

  const onCancelSubscriptionMutationSettled = () => {
    removeLoadingToast();

    setLoading(false);
  };

  const cancelSubscriptionMutation = useMutation(callCancelSubscription, {
    onSuccess: onCancelSubscriptionMutationSuccess,
    onError: onCancelSubscriptionMutationError,
    onSettled: onCancelSubscriptionMutationSettled,
  });

  const handleConfirmCancellationClick = () => {
    if (isEligibleForPrepaidCancellation) {
      loadNextFlowStep();
      return;
    }
    cancelSubscriptionMutation.mutate({
      customerId,
      subscriptionId,
      authToken,
    });
  };

  return (
    <>
      <ModalHeader>{t('cancelModal.confirm.modalTitle')}</ModalHeader>
      <ModalCloseButton />
      <ModalBody>
        <Box>
          <Text variant="strong">{t('cancelModal.confirm.title')}</Text>
          <Textarea
            height="120px"
            name="details"
            onChange={handleChange}
            value={values.details}
          />
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button mr="4" onClick={loadPreviousFlowStep} variant="outlinedBig">
          {t('cancelModal.back')}
        </Button>
        <Button
          isLoading={loading}
          onClick={handleConfirmCancellationClick}
          variant={isEligibleForPrepaidCancellation ? 'blackBig' : 'dangerBig'}
        >
          {isEligibleForPrepaidCancellation
            ? t('cancelModal.confirm.buttonContinue')
            : t('cancelModal.confirm.buttonConfirm')}
        </Button>
      </ModalFooter>
    </>
  );
};

export default ConfirmCancellationModal;
